import * as Configuration from "./configuration"
import { Names as DebtFormNames } from "./form-parts/debt-form-part"
import { Names as GeneralitiesFormNames } from "./form-parts/generalities-form-part"
import { Names as ObservationFormNames } from "./form-parts/observation-form-part"
import { Names as PpiNames } from "./form-parts/ppi-form-part"
import { Names as BudgetNames} from './form-parts/budget-form-part'
import { Names as TableNames } from "./form-parts/table-form-part"
import { Names as DobNames } from "./form-parts/dob-from-part"
import { createYears } from "./generator/utils"
import * as AllocationDomain from "../../domain/sfp/allocation-domain"
import * as ChapterDomain from "../../domain/sfp/chapter-domain"
import * as PpiDomain from "../../domain/sfp/ppi-domain"
import * as TaxDomain from "../../domain/sfp/tax-domain"
import * as Model from "../../model"
import { CSSProperties } from "react"

export function configure(
	formData: FormData,
	expenseChapters: Model.Chapter[],
	receiptsChapters: Model.Chapter[],
	themes: Model.PpiInvestmentTheme[]
): Configuration.ReportConfiguration {
	return {
		currency: "EUR",
		debt: configureDebt(formData),
		generalities: configureGeneralities(formData),
		locale: navigator.language,
		observation: configureObservation(
			formData, expenseChapters, receiptsChapters),
		ppi: configurePpi(formData, themes),
		table: configureTable(formData),
		budgetAnnexe: configureBudgetAnnex(formData),
		dob:configureDob(formData)
	}
}

function configureGeneralities(
	formData: FormData
): Configuration.GeneralitiesConfiguration {
	const conf: Configuration.GeneralitiesConfiguration = {
		date: configureDate(formData),
		glossary: formData.has(GeneralitiesFormNames.Glossary),
		logo: configureLogo(formData),
		pagination: formData.has(GeneralitiesFormNames.Pagination),
		referenceYear: parseInt(
			formData.get(GeneralitiesFormNames.ReferenceYear).toString()
		),
		subtitle: configureSubtitle(formData),
		summary: formData.has(GeneralitiesFormNames.Summary),
		title: configureTitle(formData),
		years: createYears(
			parseInt(formData.get(GeneralitiesFormNames.Years.Start).toString()),
			parseInt(formData.get(GeneralitiesFormNames.Years.End).toString())
		),
	}

	if (formData.has(GeneralitiesFormNames.RoundnessEnabled))
		conf.roundness = formData.get(GeneralitiesFormNames.Roundness).toString() as Configuration.RoundnessValue

	if (formData.has(GeneralitiesFormNames.HideZeroLinesEnabled))
		conf.enableHideZeroLines = formData.get(GeneralitiesFormNames.HideZeroLinesEnabled) === 'on' ? true : false
	
	return conf
}

function configureObservation(
	formData: FormData,
	expenseChapters: Model.Chapter[],
	receiptsChapters: Model.Chapter[]
): Configuration.ObservationConfiguration {
	const conf: Configuration.ObservationConfiguration = {
		allocationsCharts: configureAllocations(formData),
		autoFinancing: {
			globalCharts: formData.has(ObservationFormNames.AutoFinancing.Charts.Global) ,
			tables: formData.has(ObservationFormNames.AutoFinancing.Tables)
		},
		investmentSection: {
			globalCharts: formData.has(ObservationFormNames.Investment.Charts.Global),
			tables: formData.has(ObservationFormNames.Investment.Tables)
		},
		operationSection: {
			expense: configureOperationExpense(formData, expenseChapters),
			receipts: configureOperationReceipts(formData, receiptsChapters)
		},
		taxCharts: configureTax(formData),
		comparaison:configureCompare(formData),
		synthesis:configureSynthesis(formData),
	}

	return conf
}

function configureAllocations(formData: FormData): Configuration.AllocationsConfiguration {
	const allocations = formData
		.getAll(ObservationFormNames.Allocations.Charts.Allocations)
		.map(v => v.toString() as AllocationDomain.Allocation)

	return {
		allocations,
		global: formData.has(ObservationFormNames.Allocations.Charts.Global),
		onePerAllocation: formData.has(ObservationFormNames.Allocations.Charts.OnePerAllocation),
		onePerAllocationPerInhabitant: formData.has(ObservationFormNames.Allocations.Charts.OnePerAllocationPerInhabitant)
	}
}

function configureDate(formData: FormData): Date {
	// si le formulaire n'a pas de champ eneralitiesNames.Date
	if (!formData.has(GeneralitiesFormNames.Date)) return null

	const date = new Date(formData.get(GeneralitiesFormNames.Date).toString());

	return isNaN(date.getTime()) ? null : date
}

function configureBudgetAnnex (
	formData: FormData,
){
	return {
		budgetsAnnexes: formData.has(BudgetNames.budgetsAnnexes),
		dettesReelles: formData.has(BudgetNames.dettesReelles),
		dettesSimulées: formData.has(BudgetNames.dettesSimulées),
		dettesGlobales: formData.has(BudgetNames.dettesGlobales),
	}
}

function configureDebt(formData: FormData): Configuration.DebtConfiguration {
	return {
		annuity: {
			globalCharts: formData.has(DebtFormNames.Annuities.Charts.Global),
			perBank: formData.has(DebtFormNames.Annuities.Charts.PerBank),
			perRateType: formData.has(DebtFormNames.Annuities.Charts.PerRateType),
			tables: formData.has(DebtFormNames.Annuities.Tables),
			PieChartType:formData.has(DebtFormNames.Annuities.Charts.PieChartType)
		},
		capitalReimbursement: {
			globalCharts: formData.has(DebtFormNames.CapitalReimbursement.Charts.Global),
			perBank: formData.has(DebtFormNames.CapitalReimbursement.Charts.PerBank),
			perRateType: formData.has(DebtFormNames.CapitalReimbursement.Charts.PerRateType),
			tables: formData.has(DebtFormNames.CapitalReimbursement.Tables),
			PieChartType:formData.has(DebtFormNames.Annuities.Charts.PieChartType)
		},
		enableProspective: formData.has(DebtFormNames.ProspectiveEnabled),
		enableRetrospective: formData.has(DebtFormNames.RetrospectiveEnabled),
		enableSimulatedLoans: formData.has(DebtFormNames.SimulatedLoansEnabled),
		interestReimbursement: {
			globalCharts: formData.has(DebtFormNames.InterestsReimbursement.Charts.Global),
			perBank: formData.has(DebtFormNames.InterestsReimbursement.Charts.PerBank),
			perRateType: formData.has(DebtFormNames.InterestsReimbursement.Charts.PerRateType),
			tables: formData.has(DebtFormNames.InterestsReimbursement.Tables),
			PieChartType:formData.has(DebtFormNames.Annuities.Charts.PieChartType)
		},
		remainingCapital: {
			globalCharts: formData.has(DebtFormNames.RemainingCapital.Charts.Global),
			perBank: formData.has(DebtFormNames.RemainingCapital.Charts.PerBank),
			perRateType: formData.has(DebtFormNames.RemainingCapital.Charts.PerRateType),
			tables: formData.has(DebtFormNames.RemainingCapital.Tables),
			PieChartType:formData.has(DebtFormNames.Annuities.Charts.PieChartType)
		}
	}
}

function configureLogo(formData: FormData): Configuration.LogoConfiguration {
	if (!formData.has(GeneralitiesFormNames.LogoEnabled)) return null
	if (!formData.has(GeneralitiesFormNames.LogoData)) return null
	if (!formData.has(GeneralitiesFormNames.LogoHeight)) return null
	if (!formData.has(GeneralitiesFormNames.LogoWidth)) return null

	return {
		data: formData.get(GeneralitiesFormNames.LogoData).toString(),
		height: parseFloat(formData.get(GeneralitiesFormNames.LogoHeight).toString()),
		width: parseFloat(formData.get(GeneralitiesFormNames.LogoWidth).toString())
	}
}

function configureOperationExpense(
	formData: FormData,
	chapters: Model.Chapter[]
): Configuration.OperationSectionConfiguration {
	const chapterIds = formData
		.getAll(ObservationFormNames.OperationExpense.Charts.Chapters)
		.map(v => parseInt(v.toString()))

	return {
		charts: {
			chapters: ChapterDomain.filterChaptersByIds(chapters, chapterIds),
			global: formData.has(ObservationFormNames.OperationExpense.Charts.Global),
			onePerChapter: formData.has(ObservationFormNames.OperationExpense.Charts.OnePerChapter),
			onePerChapterPerInhabitant: formData.has(ObservationFormNames.OperationExpense.Charts.OnePerChapterPerInhabitant),
		},
		tables: formData.has(ObservationFormNames.OperationExpense.Tables),
		blankTable: formData.has(ObservationFormNames.ProgressOperationExpense.Tables) 
	}
}

function configureOperationReceipts(
	formData: FormData,
	chapters: Model.Chapter[]
): Configuration.OperationSectionConfiguration {
	const chapterIds = formData
		.getAll(ObservationFormNames.OperationReceipts.Charts.Chapters)
		.map(v => parseInt(v.toString()))

	return {
		tables: formData.has(ObservationFormNames.OperationReceipts.Tables),
		blankTable: formData.has(ObservationFormNames.ProgressOperationReceipts.Tables),
		charts: {
			chapters: ChapterDomain.filterChaptersByIds(chapters, chapterIds),
			global: formData.has(ObservationFormNames.OperationReceipts.Charts.Global),
			onePerChapter: formData.has(ObservationFormNames.OperationReceipts.Charts.OnePerChapter),
			onePerChapterPerInhabitant: formData.has(ObservationFormNames.OperationReceipts.Charts.OnePerChapterPerInhabitant),
		}
	}
}

function configureDob(
	formData: FormData,
): Configuration.DobConfiguration {
	
	return {
		table: formData.has(DobNames.DOB.Tables)
	}
}

function configurePpi(
	formData: FormData,
	themes: Model.PpiInvestmentTheme[]
): Configuration.PpiConfiguration {
	const themeIds = formData
	.getAll(PpiNames.InvestmentExpense.Tables)
	.map(v => parseInt(v.toString()))

	const selectedThemes = PpiDomain.filterThemesAndSubthemesByIds(
		themes, themeIds)

	const projectIds = formData
	.getAll(PpiNames.InvestmentProjects.Tables)
	.map(v => parseInt(v.toString()))

	const projects = PpiDomain.findThemesProjectsByIds(themes, projectIds)

	return {
		expenseThemes: selectedThemes,
		generalTable: formData.has(PpiNames.InvestmentGeneralPpi.Tables),
		projects: PpiDomain.filterProjectsByIds(projects, projectIds),
		years: formData.getAll(PpiNames.Years).map(e => parseInt(e.toString())),
		priorities: formData.getAll(PpiNames.Priorities).map(e => parseInt(e.toString()))	}
}

function configureSubtitle(formData: FormData): string {
	// si le formulaire n'a pas de champ GeneralitiesNames.SubtitleEnabled
	if (!formData.has(GeneralitiesFormNames.SubtitleEnabled)) return null

	// si le formulaire n'a pas de champ GeneralitiesNames.Subtitle
	if (!formData.has(GeneralitiesFormNames.Subtitle)) return null

	const subtitle = formData.get(GeneralitiesFormNames.Subtitle).toString()

	return "" !== subtitle ? subtitle : null
}

function configureTable(formData: FormData): Configuration.TableConfiguration {
	const horizontalPadding = "3px"
	const verticalPadding = "4px"
	const common: CSSProperties = {
		borderColor: formData.get(TableNames.TableBorderColor).toString(),
		borderWidth: "1pt",
		borderStyle: "solid",
		color: formData.get(TableNames.TableTextColor).toString(),
		fontFamily: "Calibri",
		fontSize: 8,
		paddingBottom: verticalPadding,
		paddingLeft: horizontalPadding,
		paddingRight: horizontalPadding,
		paddingTop: verticalPadding,
	}
	const header: CSSProperties = {
		...common,
		backgroundColor: formData
		.get(TableNames.TableHeaderBackgroundColor)
		.toString(),
		color: formData.get(TableNames.TableHeaderTextColor).toString(),
		fontWeight: "bold",
		textAlign: "center"
	}
	const negative: CSSProperties = { color: "#e56b53" }
	const positive: CSSProperties = { color: "#bcce47" }
	const numeric: CSSProperties = { textAlign: "right" }
	const subtotal: CSSProperties = {
		...header,
		backgroundColor: formData.get(TableNames.TableSubtotalBackgroundColor)
		.toString(),
		color: formData.get(TableNames.TableSubtotalTextColor).toString(),
		textAlign: "left"
	}
	const text: CSSProperties = { textAlign: "left" }
	const total: CSSProperties = { ...header, textAlign: "left" }
	const ratio:CSSProperties = { ...common,textAlign:"left", backgroundColor:"#8ea9db"}

	return {
		common,
		header,
		negative,
		numeric,
		positive,
		subtotal,
		text,
		total,
		ratio
	}
}

function configureTax(formData: FormData): Configuration.TaxConfiguration {
	const taxes = formData
		.getAll(ObservationFormNames.Tax.Charts.Taxes)
		.map(v => v.toString() as TaxDomain.Tax)

	return {
		global: formData.has(ObservationFormNames.Tax.Charts.Global),
		onePerTax: formData.has(ObservationFormNames.Tax.Charts.OnePerTax),
		onePerTaxPerInhabitant: formData.has(
			ObservationFormNames.Tax.Charts.OnePerTaxPerInhabitant),
		taxes
	}
}

function configureCompare(formData:FormData){
	return{
		tables: formData.has(ObservationFormNames.OperationCompare.Tables),
		blankTable: formData.has(ObservationFormNames.OperationCompare.BlankTable)
	}
}

function configureSynthesis(formData:FormData){
	return{		
		synthesis: formData.has(ObservationFormNames.Synthesis.Synthesis)
	}
}

function configureTitle(formData: FormData): string {
	// si le formulaire n'a pas de champ GeneralitiesNames.TitleEnabled
	if (!formData.has(GeneralitiesFormNames.TitleEnabled)) return null

	// si le formulaire n'a pas de champ GeneralitiesNames.Title
	if (!formData.has(GeneralitiesFormNames.Title)) return null

	const title = formData.get(GeneralitiesFormNames.Title).toString()

	return "" !== title ? title : null
}



// WEBPACK FOOTER //
// ./src/component/management-report/configurator.ts